import {
    IStrapiEntity,
    IStrapiFile,
    IStrapiRelationship,
    StrapiBaseFields,
} from "services/strapi"
import {IUser} from "features/users"
import {ITask} from "features/tasks"
import {IConcierge} from "features/concierges"
import {IDocument} from "features/documents"
import {IAddress} from "components/common"

export interface IHome extends StrapiBaseFields {
    attomAvm?: number
    zillowAvm?: number
    attomId?: string
    zillowId: string
    zillowUrl: string
    apn: string
    streetAddress?: string
    city: string
    state: string
    zip: string
    beds?: number
    fullBaths?: number
    partialBaths?: number
    squareFootage?: number
    yearBuilt?: string | number
    floors?: string | number
    basementType: string
    basementSquareFootage?: number
    hasHoa?: boolean | string
    hoaType: string
    parkingSpaces?: string | number
    hasSolar?: boolean | string
    estimatedValue: string
    estimatedRentalValue: string
    county: string
    longitude: string
    latitude: string
    subdivisionName: string
    subdivisionId: string
    lotSize: string
    propertyType: string
    parkingType: string
    basementFinishedPercentage: string
    lastSoldDate: string
    originalMortgageBalance: string
    currentMortgageBalance: string
    equity: string
    equityPercentage: string
    sellStatus: "simple" | "cash"
    conciergeId: string
    recentSolds?: IHomeRecentlySoldNear[]
    expectedOwners?: [string]
    tasks?: IStrapiRelationship<IStrapiEntity<ITask>[]>
    documents?: IStrapiRelationship<IStrapiEntity<IDocument>[]>
    concierge?: IStrapiRelationship<IStrapiEntity<IConcierge>>
    user?: IStrapiRelationship<IStrapiEntity<IUser>>
    valuationRequested: boolean
    deedBook: string
    deedPage: string
    cmaUrl: string
}

export interface IRecents {
    recentlySold: IRecent,
    recentlyListed: IRecent,
}

export interface IRecent {
    success: boolean,
    status: number,
    bundle: any[],
    total: number
}

export interface IBundle {
    BathroomsFull: number,
    StreetDirPrefix: string,
    StreetName: string,
    AboveGradeFinishedArea: number,
    BedroomsTotal: number,
    BridgeModificationTimestamp: string,
    Media: IBundleResource[]
    Latitude: number,
    ListPrice: number,
    Longitude: number,
    StandardStatus: string,
    CloseDate: string,
    StreetSuffix: string,
    StreetNumber: string,
    ListingId: string,
    UnitNumber: string,
    StreetDirSuffix: string,
    YearBuilt: number,
    ListingKey: string,
    distanceFrom: number,
    FeedTypes: string[],
    StateOrProvince: string,
    PostalCode:string,
    City:string,
    url: string

}

export interface IBundleResource {
    Order: 44,
    MediaURL: string,
    ResourceRecordKey: string,
    ResourceName: string,
    OriginatingSystemMediaKey: string,
    MimeType: string,
    ShortDescription: string,
    MediaKey: string,
    LongDescription: string,
    MediaKeyNumeric: number,
    Permission: string[],
    ClassName: string,
    ImageOf: string,
    ModificationTimestamp: string,
    MediaCategory: string,
    MediaObjectID: string
}

export interface IHomePricingModel {
    recommendation: number
    averageSellDays: number
    comps: any
    home: IStrapiRelationship<IHome>
    userListPrice: number
    id: number
}

export interface IHomeListing {
    id: number
    photosApproved: boolean
    descriptionApproved: boolean
    home: IHome
    photos: IStrapiFile[]
    description: string
}

export interface IHomeSellScore {
    home: IHome
    score: number
    voidedOn: Date
}

export interface IHomeEstimatedMarketValue {
    home: IHome
    price: number
}

export interface IAddHomeForm extends IAddress {
    hasCoOwners: boolean
    coOwners?: ICoOwner[]
}

export interface ICoOwner {
    id?: number
    phoneNumber: string
    emailAddress: string
    firstName: string
    lastName: string
    fullName: string
}

export interface IHomeRecentlySoldNear extends IHomeSellScore {
    streetAddress: string
    soldAmount: number
    soldDate: string
}

export interface IHomeFormValues extends Partial<Omit<IHome, "users">> {
    id?: number
}

export interface IHomeUpdateValues extends Partial<Omit<IHome, "users">> {
    id: number
    values: Partial<Omit<IHome, "users">>
}

export interface IHomeCashOfferFormValues {
    id: number
    cashAmount: string
    isOwner: boolean
    relationshipToOwner: string
    propertyType: string
    kitchen: string
    livingRoom: string
    mainBathroom: string
    homeExterior: string
    hasHoa: boolean
    otherDetails?: string[]
    sellTimeline: string
}

export interface IHomeSimpleSaleSurveyValues {
    id: number
    buy: string
    hasBuyerAgent?: string
    wantsBuyerAgent?: string
    primaryResidence: string
    homeOwnershipTime: string
    reason: string
    timeline: string
}

export enum EPropertyType {
    SingleFamilyHome = "singleFamilyHome",
    MultiFamilyHome = "multiFamilyHome",
    Condo = "condo",
    Townhome = "townhome",
    Other = "other",
}

export enum EPropertyRelationship {
    Spouse = "spouse",
    ListingAgent = "listingAgent",
    Friend = "friend",
    Relative = "relative",
}

export enum EPropertyDescription {
    FixerUpper = "fixerUpper",
    Dated = "dated",
    Standard = "standard",
    HighEnd = "highEnd",
    Luxury = "luxury",
}

export enum EPropertyDescriptionKeys {
    Kitchen = "kitchen",
    LivingRoom = "livingRoom",
    MainBathroom = "mainBathroom",
    HomeExterior = "homeExterior",
}

export const propertyDescription = {
    fixerUpper: "needs significant repairs",
    dated: "hasn't been updated recently",
    standard: "is updated with common finishes",
    highEnd: "has high-quality upgrades",
    luxury: "has elegant, top-tier finishes",
}

export const otherDetails = {
    leasedOrFinancedSolarPanels: {
        title: "Leased or financed solar panels",
    },
    knownFoundationIssues: {
        title: "Known foundation issues",
        description: "Excessive cracking, uneven floors",
    },
    fireDamage: {
        title: "Fire Damage",
    },
    wellWater: {
        title: "Well water",
        description: "Your client maintains a well to supply water",
    },
    septicSystem: {
        title: "Septic system",
        description: "Separate from municipal sewage",
    },
    asbestosSiding: {
        title: "Asbestos siding",
    },
    horseProperty: {
        title: "Horse property",
        description: "Livestock live on property",
    },
    mobileOrManufacturedHome: {
        title: "Mobile or manufactured home",
    },
}

export interface IHomeFormReviewPricingFormValues {
    homeId: number
    homePricingModelId: number
    taskId: number
    userListPrice: number
    tentativeActiveListingDate?: Date
}

export interface IHomeFormReviewListingFormValues {
    homeId: number
    homeListingId?: number
    taskId: number
    photosApproved: boolean | string
    descriptionApproved: boolean | string
}

export interface IHomeFormScheduleSignLockboxDeliveryFormValues {
    id: number
    taskId: number
    schedule: string
}

export interface IHomeFormListingPaymentValues {
    id: number
    taskId: number
    date: Date
    hour: number
    minute: number
    comments: string
}

export interface ICreatePaymenetIntentValues {
    id: number
    taskId: number
    date: Date
    comments: string
}

export interface IHomeFormSchedulePhotosValues {
    id: number
    taskId: number
    phototype: "standard" | "luxury"
    types: string[] // ["photo", "video", "aerial", "360", "floor", "twilight"]
    propertysize: 1 | 2 | 3 // small, medium, large
    aerialtype?: 1 | 2 | 3 // photos only, video only, photos and video
}

export interface IHomeFormVerifyIdentityValues {
    id: number
    taskId: number
    isUserReal: string
}

export interface IUserClickEvent {
    homeId: number | undefined
    eventType: EUserEventType
    data?: any
}

export enum EUserEventType {
    ViewedFulLReport = "viewed_full_report",
    CmaRequested = "cma_requested",
    ViewedSellingOptions = "viewed_selling_options",
    ViewedRefinanceOptions = "viewed_refinance_options",
    ViewedCma = "viewed_cma",
    SentMessage = "sent_message",
    RequestListingConsultation = "request_listing_consultation",
    RequestRefiConsultation = "request_refi_consultation"
}